import { useQuery } from '@apollo/client';
import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useEffect, useState } from 'react';
import { useAsync, useDeepCompareEffect } from 'react-use';

import { useTracking } from '@hooks/useTracking';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';

import { useAuth } from '@providers/AuthProvider';

import { IPageProps } from '@folx-types/flow-steps';
import { IAccountInfoQueryData } from '@folx-types/member';

import ACCOUNT_INFO_QUERY from '@graphql/queries/AccountInfo.query.gql';
import GET_CONTACTS from '@graphql/queries/getEmergencyContacts.query.gql';

import AccountInfo, { IAccountInfoContent } from './AccountInfo.component';

interface IAccountInfoProps extends IPageProps {}

const AccountInfoPage: FC<IAccountInfoProps> = () => {
	const [isPdf, setIsPdf] = useState(false);

	const { getAuthToken } = useAuth();

	const {
		loading: accountLoading,
		data: accountData,
		error: accountError,
	} = useQuery<IAccountInfoQueryData>(ACCOUNT_INFO_QUERY, {
		fetchPolicy: 'cache-and-network',
	});
	const member = accountData?.currentMember;

	const {
		data: contactsData,
		loading: contactsLoading,
		error: contactsError,
	} = useQuery(GET_CONTACTS);
	const emergencyContacts = contactsData?.emergencyContacts?.nodes;

	const { trackEvent } = useTracking('member-portal-view');

	const pageContent = useStaticQuery<Record<string, string>>(
		graphql`
			{
				butterPage(slug: { eq: "mssp-account-page" }) {
					heading
					profile_picture_label
					name_label
					pronouns_label
					email_label
					phone_number_label
					sms_opt_in_label
					innerspace_label
					government_id_label
					ssn_label
					legal_name_label
					emergency_label
					email_us_disclaimer
					empty_contacts_text
					contact_tooltip_title
					contact_tooltip_description
				}
			}
		`
	);

	const {
		value: selfie,
		error: selfieError,
		loading: selfieLoading,
	} = useAsync(async () => {
		const authorizationToken = await getAuthToken();
		const selfieData = await axios.get(`${ApiEndpoints.memberPortal}/image/selfieImage`, {
			headers: {
				Accept: 'image/png, image/jpeg, image/gif, application/pdf',
				authorizationToken,
			},
			responseType: 'blob',
		});

		if (!selfieData) return;
		return URL.createObjectURL(selfieData.data);
	}, []);

	useDeepCompareEffect(() => {
		if (!selfieError) return;
		console.log(selfieError);
	}, [selfieError]);

	const {
		value: idPhoto,
		error: idPhotoError,
		loading: idPhotoLoading,
	} = useAsync(async () => {
		const authorizationToken = await getAuthToken();
		const idPhotoData = await axios.get(`${ApiEndpoints.memberPortal}/image/idImage`, {
			headers: {
				Accept: 'image/png, image/jpeg, image/gif, application/pdf',
				authorizationToken,
			},
			responseType: 'blob',
		});

		if (!idPhotoData) return;
		return URL.createObjectURL(idPhotoData.data);
	}, []);

	useDeepCompareEffect(() => {
		if (!idPhotoError) return;
		console.log(idPhotoError);
	}, [idPhotoError]);

	useEffect(() => {
		if (!idPhoto) {
			return;
		}
		const getBlob = async () => {
			return await (await fetch(idPhoto)).blob();
		};

		getBlob().then((blob) => {
			return setIsPdf(blob.type === 'application/pdf');
		});
	}, [idPhoto]);

	const loading = accountLoading || selfieLoading || idPhotoLoading || contactsLoading;

	const refinedContent: IAccountInfoContent = keysToCamel(pageContent) as IAccountInfoContent;

	return (
		<AccountInfo
			emergencyContacts={emergencyContacts}
			member={member}
			selfie={selfie}
			pageContent={refinedContent}
			loading={loading}
			idPhoto={idPhoto}
			isPdf={isPdf}
		/>
	);
};

export default AccountInfoPage;
